export class PushNotificationsPwaUrlStore {
  private readonly dbName = '_ionicstorage';
  private readonly storeName = '_ionickv';
  private readonly keyName = 'api-url';
  private readonly targetVersion = 1;
  private readonly maxAttempts = 10;

  public async saveUrl(url: string): Promise<void> {
    if (typeof url !== 'string' || url.trim() === '') {
      throw new TypeError('URL должен быть непустой строкой');
    }

    let db: IDBDatabase | null = null;

    try {
      db = await this.ensureStoreExists();
      await this.setItem(db, this.storeName, this.keyName, url);
    } catch (error) {
      throw new Error(`Ошибка сохранения URL: ${error instanceof Error ? error.message : String(error)}`);
    } finally {
      db?.close();
    }
  }

  // --------------------------------------------------------------------------
  //  Приватные методы — управление БД
  // --------------------------------------------------------------------------

  /**
   * Открывает соединение с IndexedDB и гарантирует существование objectStore.
   * При необходимости повышает версию БД и создаёт хранилище.
   */
  private async ensureStoreExists(): Promise<IDBDatabase> {
    if (typeof window === 'undefined' || !window.indexedDB) {
      throw new Error('IndexedDB не поддерживается в этом окружении');
    }

    // 1. Пытаемся открыть с целевой версией 2
    try {
      const db = await this.openWithUpgrade(this.targetVersion);
      if (db.objectStoreNames.contains(this.storeName)) {
        return db;
      }
      db.close();
    } catch (error) {
      // VersionError — версия БД уже >=2, продолжаем
      if (!(error instanceof DOMException && error.name === 'VersionError')) {
        throw error;
      }
    }

    // 2. Открываем с текущей версией, проверяем наличие хранилища
    let currentVersion: number;
    {
      const db = await this.openWithCurrentVersion();
      const hasStore = db.objectStoreNames.contains(this.storeName);
      currentVersion = db.version;
      if (hasStore) {
        return db;
      }
      db.close();
    }

    // 3. Циклически повышаем версию, пока не создадим хранилище
    for (let attempt = 0; attempt < this.maxAttempts; attempt++) {
      const nextVersion = currentVersion + 1;
      try {
        const db = await this.openWithUpgrade(nextVersion);
        if (db.objectStoreNames.contains(this.storeName)) {
          return db;
        }
        // upgradeneeded не вызвался (версия не увеличилась) или хранилище не создано
        currentVersion = db.version;
        db.close();
      } catch (error) {
        // Если ошибка версии — обновляем currentVersion и продолжаем
        if (error instanceof DOMException && error.name === 'VersionError') {
          // Узнаём актуальную версию
          const tempDb = await this.openWithCurrentVersion();
          currentVersion = tempDb.version;
          tempDb.close();
        } else {
          throw error;
        }
      }
    }

    throw new Error(`Не удалось создать хранилище "${this.storeName}" после ${this.maxAttempts} попыток`);
  }

  /**
   * Открывает БД с указанной версией.
   * В upgradeneeded создаёт хранилище, если оно отсутствует.
   */
  private openWithUpgrade(version: number): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      const request = window.indexedDB.open(this.dbName, version);

      request.onerror = () => reject(request.error);
      request.onblocked = () => reject(new Error('Открытие базы заблокировано'));

      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains(this.storeName)) {
          // Создаём хранилище в формате Ionic Storage (keyPath: 'key')
          db.createObjectStore(this.storeName);
        }
      };

      request.onsuccess = () => resolve(request.result);
    });
  }

  /**
   * Открывает БД с текущей версией (без повышения).
   */
  private openWithCurrentVersion(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      const request = window.indexedDB.open(this.dbName);

      request.onerror = () => reject(request.error);
      request.onblocked = () => reject(new Error('Открытие базы заблокировано'));
      request.onsuccess = () => resolve(request.result);
    });
  }

  // --------------------------------------------------------------------------
  //  Приватный статический метод — запись
  // --------------------------------------------------------------------------

  /**
   * Сохраняет значение в object store.
   * Поддерживает keyPath = null (внешний ключ) и keyPath = 'key' (Ionic Storage).
   */
  private setItem(db: IDBDatabase, storeName: string, key: string, value: string): Promise<void> {
    return new Promise((resolve, reject) => {
      // Проверка наличия хранилища (на всякий случай, хотя вызывающий код гарантирует)
      if (!db.objectStoreNames.contains(storeName)) {
        reject(new Error(`Хранилище "${storeName}" не существует`));
        return;
      }

      const transaction = db.transaction(storeName, 'readwrite');
      const store = transaction.objectStore(storeName);
      let request: IDBRequest<IDBValidKey | { key: string; value: string }>;

      if (store.keyPath === null) {
        request = store.put(value, key);
      } else if (store.keyPath === 'key') {
        request = store.put({ key, value });
      } else {
        reject(
          new Error(`Хранилище "${storeName}" имеет keyPath = "${String(store.keyPath)}", ожидается null или 'key'`)
        );
        return;
      }

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
      transaction.onerror = () => reject(transaction.error);
    });
  }
}
